import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, Target } from 'lucide-react';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Scatter } from 'recharts';
// Removed guideline targets - using only client goals

interface DayNutritionAnalysisProps {
  dayNumber: number;
  date: string;
  dayNutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sodium?: number;
    sugar?: number;
    cholesterol?: number;
    saturatedFat?: number;
    calcium?: number;
    vitaminB12?: number;
    phosphorus?: number;
  };
  clientGoals: {
    eerGoalCalories: number;
    proteinGoalMin: number;
    proteinGoalMax: number;
    carbsGoalMin: number;
    carbsGoalMax: number;
    fatGoalMin: number;
    fatGoalMax: number;
    fiberGoalGrams: number;
  };
  clientDetails?: {
    eerCalories?: number;
    macrosRanges?: {
      protein?: { min?: number; max?: number; unit?: string };
      carbs?: { min?: number; max?: number; unit?: string };
      fat?: { min?: number; max?: number; unit?: string };
      fiber?: { min?: number; max?: number; unit?: string };
      saturatedFat?: { min?: number; max?: number; unit?: string };
      cholesterol?: { min?: number; max?: number; unit?: string };
      calcium?: { min?: number; max?: number; unit?: string };
      vitaminB12?: { min?: number; max?: number; unit?: string };
      phosphorus?: { min?: number; max?: number; unit?: string };
    };
  };
}

const DayNutritionAnalysis: React.FC<DayNutritionAnalysisProps> = ({
  dayNumber,
  date,
  dayNutrition,
  clientGoals,
  clientDetails,
}) => {
  const evaluateStatus = (
    value: number,
    clientMin: number,
    clientMax: number,
  ): 'low' | 'normal' | 'high' => {
    if (clientMin > 0 && value < clientMin) {
      return 'low';
    }
    if (clientMax > 0 && value > clientMax) {
      return 'high';
    }
    return 'normal';
  };

  const chartData = useMemo(() => {
    const macrosRanges = clientDetails?.macrosRanges || {};

    return [
      {
        name: 'Carbs',
        consumed: Number(dayNutrition.carbs),
        unit: 'g',
        targetMin: Number(clientGoals?.carbsGoalMin || 0),
        targetMax: Number(clientGoals?.carbsGoalMax || 0),
        clientGoalMin: Number(clientGoals?.carbsGoalMin || 0),
        clientGoalMax: Number(clientGoals?.carbsGoalMax || 0),
        clientGoalsMinMax: `${clientGoals?.carbsGoalMin || 0}-${clientGoals?.carbsGoalMax || 0}g`,
        status: evaluateStatus(
          Number(dayNutrition.carbs),
          Number(clientGoals?.carbsGoalMin || 0),
          Number(clientGoals?.carbsGoalMax || 0),
        ),
      },
      {
        name: 'Protein',
        consumed: Number(dayNutrition.protein),
        unit: 'g',
        targetMin: Number(clientGoals?.proteinGoalMin || 0),
        targetMax: Number(clientGoals?.proteinGoalMax || 0),
        clientGoalMin: Number(clientGoals?.proteinGoalMin || 0),
        clientGoalMax: Number(clientGoals?.proteinGoalMax || 0),
        clientGoalsMinMax: `${clientGoals?.proteinGoalMin || 0}-${clientGoals?.proteinGoalMax || 0}g`,
        status: evaluateStatus(
          Number(dayNutrition.protein),
          Number(clientGoals?.proteinGoalMin || 0),
          Number(clientGoals?.proteinGoalMax || 0),
        ),
      },
      {
        name: 'Fat',
        consumed: Number(dayNutrition.fat),
        unit: 'g',
        targetMin: Number(clientGoals?.fatGoalMin || 0),
        targetMax: Number(clientGoals?.fatGoalMax || 0),
        clientGoalMin: Number(clientGoals?.fatGoalMin || 0),
        clientGoalMax: Number(clientGoals?.fatGoalMax || 0),
        clientGoalsMinMax: `${clientGoals?.fatGoalMin || 0}-${clientGoals?.fatGoalMax || 0}g`,
        status: evaluateStatus(
          Number(dayNutrition.fat),
          Number(clientGoals?.fatGoalMin || 0),
          Number(clientGoals?.fatGoalMax || 0),
        ),
      },
      {
        name: 'Saturated Fat',
        consumed: Number(dayNutrition.saturatedFat || 0),
        unit: 'g',
        targetMin: 0,
        targetMax: 0,
        clientGoalMin: 0,
        clientGoalMax: 0,
        clientGoalsMinMax: '0g',
        status: evaluateStatus(Number(dayNutrition.saturatedFat || 0), 0, 0),
      },
      {
        name: 'Fiber',
        consumed: Number(dayNutrition.fiber),
        unit: 'g',
        targetMin: Number(clientGoals?.fiberGoalGrams || 0),
        targetMax: Number(clientGoals?.fiberGoalGrams || 0),
        clientGoalMin: Number(clientGoals?.fiberGoalGrams || 0),
        clientGoalMax: Number(clientGoals?.fiberGoalGrams || 0),
        clientGoalsMinMax: `${clientGoals?.fiberGoalGrams || 0}g`,
        status: evaluateStatus(
          Number(dayNutrition.fiber),
          Number(clientGoals?.fiberGoalGrams || 0),
          Number(clientGoals?.fiberGoalGrams || 0),
        ),
      },
      {
        name: 'Calcium',
        consumed: Number(dayNutrition.calcium || 0),
        unit: macrosRanges?.calcium?.unit || 'mg',
        targetMin: Number(macrosRanges?.calcium?.min || 0),
        targetMax: Number(macrosRanges?.calcium?.max || 0),
        clientGoalMin: Number(macrosRanges?.calcium?.min || 0),
        clientGoalMax: Number(macrosRanges?.calcium?.max || 0),
        clientGoalsMinMax: `${macrosRanges?.calcium?.min || 0}-${macrosRanges?.calcium?.max || 0}${macrosRanges?.calcium?.unit || 'mg'}`,
        status: evaluateStatus(
          Number(dayNutrition.calcium || 0),
          Number(macrosRanges?.calcium?.min || 0),
          Number(macrosRanges?.calcium?.max || 0),
        ),
      },
      {
        name: 'Vitamin B12',
        consumed: Number(dayNutrition.vitaminB12 || 0),
        unit: macrosRanges?.vitaminB12?.unit || 'µg',
        targetMin: Number(macrosRanges?.vitaminB12?.min || 0),
        targetMax: Number(macrosRanges?.vitaminB12?.max || 0),
        clientGoalMin: Number(macrosRanges?.vitaminB12?.min || 0),
        clientGoalMax: Number(macrosRanges?.vitaminB12?.max || 0),
        clientGoalsMinMax: `${macrosRanges?.vitaminB12?.min || 0}-${macrosRanges?.vitaminB12?.max || 0}${macrosRanges?.vitaminB12?.unit || 'µg'}`,
        status: evaluateStatus(
          Number(dayNutrition.vitaminB12 || 0),
          Number(macrosRanges?.vitaminB12?.min || 0),
          Number(macrosRanges?.vitaminB12?.max || 0),
        ),
      },
      {
        name: 'Cholesterol',
        consumed: Number(dayNutrition.cholesterol || 0),
        unit: macrosRanges?.cholesterol?.unit || 'mg',
        targetMin: Number(macrosRanges?.cholesterol?.min || 0),
        targetMax: Number(macrosRanges?.cholesterol?.max || 0),
        clientGoalMin: Number(macrosRanges?.cholesterol?.min || 0),
        clientGoalMax: Number(macrosRanges?.cholesterol?.max || 0),
        clientGoalsMinMax: `${macrosRanges?.cholesterol?.min || 0}-${macrosRanges?.cholesterol?.max || 0}${macrosRanges?.cholesterol?.unit || 'mg'}`,
        status: evaluateStatus(
          Number(dayNutrition.cholesterol || 0),
          Number(macrosRanges?.cholesterol?.min || 0),
          Number(macrosRanges?.cholesterol?.max || 0),
        ),
      },
      {
        name: 'Phosphorus',
        consumed: Number(dayNutrition.phosphorus || 0),
        unit: macrosRanges?.phosphorus?.unit || 'mg',
        targetMin: Number(macrosRanges?.phosphorus?.min || 0),
        targetMax: Number(macrosRanges?.phosphorus?.max || 0),
        clientGoalMin: Number(macrosRanges?.phosphorus?.min || 0),
        clientGoalMax: Number(macrosRanges?.phosphorus?.max || 0),
        clientGoalsMinMax: `${macrosRanges?.phosphorus?.min || 0}-${macrosRanges?.phosphorus?.max || 0}${macrosRanges?.phosphorus?.unit || 'mg'}`,
        status: evaluateStatus(
          Number(dayNutrition.phosphorus || 0),
          Number(macrosRanges?.phosphorus?.min || 0),
          Number(macrosRanges?.phosphorus?.max || 0),
        ),
      },
    ];
  }, [
    dayNutrition.carbs,
    dayNutrition.protein,
    dayNutrition.fat,
    dayNutrition.saturatedFat,
    dayNutrition.fiber,
    dayNutrition.calcium,
    dayNutrition.vitaminB12,
    dayNutrition.cholesterol,
    dayNutrition.phosphorus,
    clientGoals?.carbsGoalMin,
    clientGoals?.carbsGoalMax,
    clientGoals?.proteinGoalMin,
    clientGoals?.proteinGoalMax,
    clientGoals?.fatGoalMin,
    clientGoals?.fatGoalMax,
    clientGoals?.fiberGoalGrams,
    clientDetails?.macrosRanges?.calcium?.min,
    clientDetails?.macrosRanges?.calcium?.max,
    clientDetails?.macrosRanges?.vitaminB12?.min,
    clientDetails?.macrosRanges?.vitaminB12?.max,
    clientDetails?.macrosRanges?.cholesterol?.min,
    clientDetails?.macrosRanges?.cholesterol?.max,
    clientDetails?.macrosRanges?.phosphorus?.min,
    clientDetails?.macrosRanges?.phosphorus?.max,
  ]);

  const getBarColor = (status: string) => {
    if (status === 'low') return '#ef4444';
    if (status === 'high') return '#f59e0b';
    return '#10b981';
  };

  return (
    <div className="space-y-6">
      {/* Day Header */}
      <div className="flex items-center gap-3 mb-4">
        <div>
          <p className="text-sm text-gray-500">Daily Nutritional Analysis</p>
        </div>
      </div>

      {/* Calorie Analysis Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            Calorie Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Current Intake</div>
              <div className="text-xs text-gray-500 mt-1">
                {clientGoals.eerGoalCalories > 0 
                  ? `${((dayNutrition.calories / clientGoals.eerGoalCalories) * 100).toFixed(0)}% of daily goal`
                  : '0% of daily goal'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {dayNutrition.calories.toFixed(0)}
              </div>
              <div className="text-xs text-gray-500">
                of {clientGoals.eerGoalCalories.toFixed(0)} kcal
              </div>
            </div>
          </div>
          
          {/* Bullet chart */}
          <div className="relative h-12">
            {(() => {
              const currentIntake = dayNutrition.calories;
              const goalCalories = clientGoals.eerGoalCalories;
              const eerCalories = clientDetails?.eerCalories ? Number(clientDetails.eerCalories) : null;
              const poorThreshold = goalCalories * 0.7;
              const goodThreshold = goalCalories * 0.9;
              const excellentThreshold = goalCalories * 1.1;
              // Use the maximum of EER or goal as the base, then add 25% buffer to ensure EER line is always visible
              const baseValue = eerCalories 
                ? Math.max(eerCalories, goalCalories)
                : goalCalories;
              const maxScale = baseValue * 1.25;
              
              return (
                <>
                  {/* Background zones */}
                  <div className="absolute inset-0 flex rounded-lg overflow-hidden">
                    <div className="h-full bg-red-100 group relative cursor-pointer" style={{ width: `${(poorThreshold / maxScale) * 100}%` }}>
                      <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                        Poor: 0 - {poorThreshold.toFixed(0)} kcal
                      </div>
                    </div>
                    <div className="h-full bg-yellow-100 group relative cursor-pointer" style={{ width: `${((goodThreshold - poorThreshold) / maxScale) * 100}%` }}>
                      <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                        Fair: {poorThreshold.toFixed(0)} - {goodThreshold.toFixed(0)} kcal
                      </div>
                    </div>
                    <div className="h-full bg-green-100 group relative cursor-pointer" style={{ width: `${((excellentThreshold - goodThreshold) / maxScale) * 100}%` }}>
                      <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                        Good: {goodThreshold.toFixed(0)} - {excellentThreshold.toFixed(0)} kcal
                      </div>
                    </div>
                    <div className="h-full bg-yellow-100 group relative cursor-pointer" style={{ width: `${((maxScale - excellentThreshold) / maxScale) * 100}%` }}>
                      <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                        Over: {excellentThreshold.toFixed(0)}+ kcal
                      </div>
                    </div>
                  </div>
                  
                  {/* Current value bar */}
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 h-6 bg-blue-600 rounded-r shadow-md group cursor-pointer"
                    style={{ width: `${Math.min((currentIntake / maxScale) * 100, 100)}%` }}
                  >
                    <div className="hidden group-hover:block absolute -top-10 right-0 bg-blue-600 text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                      Current: {currentIntake.toFixed(0)} kcal ({goalCalories > 0 ? ((currentIntake / goalCalories) * 100).toFixed(0) : 0}%)
                    </div>
                  </div>
                  
                  {/* Goal marker */}
                  <div 
                    className="absolute top-0 bottom-0 w-1 bg-black z-10 group cursor-pointer"
                    style={{ left: `${(goalCalories / maxScale) * 100}%` }}
                  >
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap">
                      Goal
                    </div>
                    <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-black text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                      Goal: {goalCalories.toFixed(0)} kcal (100%)
                    </div>
                  </div>
                  
                  {/* EER marker */}
                  {eerCalories && (() => {
                    const eerPosition = Math.min((eerCalories / maxScale) * 100, 95);
                    return (
                      <div 
                        className="absolute top-0 bottom-0 w-1 bg-orange-500 z-10 group cursor-pointer"
                        style={{ left: `${eerPosition}%` }}
                      >
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap text-orange-600" style={{ pointerEvents: 'none' }}>
                          EER
                        </div>
                        <div className="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-3 py-1 rounded text-xs whitespace-nowrap z-50">
                          EER: {eerCalories.toFixed(0)} kcal ({goalCalories > 0 ? ((eerCalories / goalCalories) * 100).toFixed(0) : 0}%)
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>
          
          {/* Legend */}
          <div className="flex justify-between text-xs text-gray-500 px-1">
            <span>Poor (&lt;70%)</span>
            <span>Fair (70-90%)</span>
            <span>Good (90-110%)</span>
            <span>Over (&gt;110%)</span>
          </div>
        </CardContent>
      </Card>

      {/* Macronutrients Analysis Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Nutritional Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8">
          <div className="h-[800px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} layout="vertical" margin={{ top: 20, right: 30, left: 120, bottom: 20 }} barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 'dataMax + 50']} tickFormatter={(value) => `${value}`} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={110}
                  tickFormatter={(value) => `${value} (${chartData.find((d) => d.name === value)?.unit})`}
                    />
                    
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-4 border-2 border-gray-200 rounded-lg shadow-xl">
                              <p className="font-bold text-lg mb-2">{data.name}</p>
                              <div className="space-y-1">
                                <p className="text-sm">
                              <span className="font-semibold">Consumed:</span> {data.consumed.toFixed(1)}
                              {data.unit}
                                </p>
                                <p className="text-sm text-green-800">
                                  <span className="font-semibold">🔵 Client Goals:</span> {data.clientGoalsMinMax}
                                </p>
                            <p
                              className={`text-sm font-semibold mt-2 ${
                                data.status === 'low' ? 'text-red-600' : data.status === 'high' ? 'text-orange-600' : 'text-green-600'
                              }`}
                            >
                              Status:{' '}
                              {data.status === 'low' ? 'Below Target' : data.status === 'high' ? 'Above Target' : 'Within Target'}
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    
                    <Bar dataKey="consumed" radius={[0, 8, 8, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.status)} />
                      ))}
                    </Bar>
                    
                <Scatter dataKey="targetMin" fill="#3b82f6" shape="triangle" r={10} />
                <Scatter dataKey="targetMax" fill="#3b82f6" shape="triangle" r={10} />

                <Scatter dataKey="clientGoalMin" fill="#166534" shape="circle" r={8} />
                <Scatter dataKey="clientGoalMax" fill="#166534" shape="circle" r={8} />
                  </ComposedChart>
            </ResponsiveContainer>
            
            {/* Legend - Positioned in top right corner */}
            <div className="absolute -top-16 right-8 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-gray-200">
              <div className="text-xs font-medium text-gray-700 mb-2">Legend</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[8px] border-l-transparent border-r-transparent border-t-blue-500"></div>
                  <span className="text-xs text-gray-600">Nutritional Targets (DRI)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-800"></div>
                  <span className="text-xs text-gray-600">Client Goals</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DayNutritionAnalysis;

